import { rhythmComposeDuration } from "./rhythmLineComposeSymbols.js";

const EPSILON = 1e-7;

export function parseRhythmTimeSignature(timeSig) {
  const [rawTop = "4", rawBottom = "4"] = String(timeSig || "4/4").split("/");
  const top = Number(String(rawTop).replace(/\D/g, "")) || 4;
  const bottom = Number(String(rawBottom).replace(/\D/g, "")) || 4;
  return { top, bottom };
}

export function rhythmMeasureQuarterBeats(timeSig) {
  const { top, bottom } = parseRhythmTimeSignature(timeSig);
  return top * (4 / bottom);
}

export function rhythmBeamDepth(symbol) {
  switch (symbol) {
    case "eighth_note":
    case "dotted_eighth_note":
      return 1;
    case "sixteenth_note":
      return 2;
    case "thirty_second_note":
      return 3;
    default:
      return 0;
  }
}

export function isBeamableRhythm(symbol) {
  return rhythmBeamDepth(symbol) > 0;
}

function parsedGrouping(grouping) {
  const raw = Array.isArray(grouping)
    ? grouping
    : typeof grouping === "string"
      ? grouping.split(/[+,\s]+/)
      : [];

  const values = raw
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);

  return values.length ? values : null;
}

function defaultGrouping(top, bottom) {
  // Grouping values are expressed in denominator-note units. This follows
  // the meter-aware behavior in the main notation editor while making odd
  // meters explicit/overridable for this rhythm-only widget.
  if (bottom === 8 || bottom === 16) {
    if (top === 3 || (top > 3 && top % 3 === 0)) {
      return Array.from({ length: Math.max(1, top / 3) }, () => 3);
    }
    if (top === 2) return [2];
    if (top === 4) return [2, 2];
    if (top === 5) return [2, 3];
    if (top === 7) return [2, 2, 3];
  }

  return Array.from({ length: top }, () => 1);
}

export function rhythmBeamGrouping(timeSig, grouping) {
  const { top, bottom } = parseRhythmTimeSignature(timeSig);
  const explicit = parsedGrouping(grouping);

  if (explicit) {
    const total = explicit.reduce((sum, value) => sum + value, 0);
    if (Math.abs(total - top) < EPSILON) return explicit;
  }

  return defaultGrouping(top, bottom);
}

export function rhythmBeamSpans(timeSig, grouping) {
  const { bottom } = parseRhythmTimeSignature(timeSig);
  const unit = 4 / bottom;
  const groups = rhythmBeamGrouping(timeSig, grouping);
  let cursor = 0;

  return groups.map((groupSize) => {
    const start = cursor;
    const end = start + groupSize * unit;
    cursor = end;
    return { start, end };
  });
}

function spanIndexForRange(start, end, spans) {
  return spans.findIndex((span) =>
    start >= span.start - EPSILON && end <= span.end + EPSILON
  );
}

export function expectedRhythmBeamLinks(symbols = [], timeSig = "4/4", grouping) {
  const safeSymbols = Array.isArray(symbols) ? symbols : [];
  const spans = rhythmBeamSpans(timeSig, grouping);
  const starts = [];
  const ends = [];
  let cursor = 0;

  safeSymbols.forEach((symbol) => {
    const duration = rhythmComposeDuration(symbol);
    starts.push(cursor);
    if (duration == null) {
      ends.push(cursor);
    } else {
      cursor += duration;
      ends.push(cursor);
    }
  });

  return Array.from({ length: Math.max(0, safeSymbols.length - 1) }, (_, index) => {
    const left = safeSymbols[index];
    const right = safeSymbols[index + 1];
    if (!isBeamableRhythm(left) || !isBeamableRhythm(right)) return false;

    const leftSpan = spanIndexForRange(starts[index], ends[index], spans);
    const rightSpan = spanIndexForRange(starts[index + 1], ends[index + 1], spans);

    return leftSpan >= 0 && leftSpan === rightSpan;
  });
}

export function normalizeRhythmBeamLinks(rawLinks, symbolCount) {
  const length = Math.max(0, Number(symbolCount) - 1);
  const links = Array.isArray(rawLinks) ? rawLinks : [];
  return Array.from({ length }, (_, index) => links[index] === true);
}
